/**
 * Skeleton component for employee detail page loading state
 * @module components/common/EmployeeDetailSkeleton
 */

import React from 'react'
import { Card } from '@/components/ui/Card'
import { SkeletonText, SkeletonRounded, SkeletonCircular } from './Skeleton'

/**
 * Employee detail loading skeleton
 *
 * Displays skeleton placeholders matching the structure of the employee detail page
 */
export const EmployeeDetailSkeleton: React.FC = () => {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <SkeletonRounded width={40} height={40} />
          <div className="space-y-2">
            <SkeletonText width={250} height={36} />
            <SkeletonText width={200} height={16} />
          </div>
        </div>
        <div className="flex gap-3">
          <SkeletonRounded width={130} height={36} />
          <SkeletonRounded width={130} height={36} />
          <SkeletonRounded width={90} height={36} />
        </div>
      </div>

      {/* Profile Card */}
      <Card className="p-6">
        <div className="flex items-start gap-6">
          {/* Avatar */}
          <SkeletonCircular width={96} height={96} />

          <div className="flex-1">
            {/* Name and Status */}
            <div className="flex items-center gap-3 mb-4">
              <SkeletonText width={200} height={28} />
              <SkeletonRounded width={80} height={24} />
            </div>

            {/* Info grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="flex items-center gap-2">
                  <SkeletonCircular width={16} height={16} />
                  <SkeletonText width={`${60 + Math.random() * 30}%`} height={16} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {/* Employment & Personal Info Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {Array.from({ length: 2 }).map((_, cardIndex) => (
          <Card key={cardIndex} className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <SkeletonCircular width={20} height={20} />
              <SkeletonText width={180} height={24} />
            </div>
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, index) => (
                <div key={index} className="flex justify-between">
                  <SkeletonText width={100} height={14} />
                  <SkeletonText width={120} height={14} />
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>

      {/* Direct Reports */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <SkeletonCircular width={20} height={20} />
          <SkeletonText width={200} height={24} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, index) => (
            <Card key={index} className="p-4">
              <div className="flex items-center gap-3">
                <SkeletonCircular width={40} height={40} />
                <div className="flex-1 min-w-0 space-y-1">
                  <SkeletonText width="80%" height={16} />
                  <SkeletonText width="60%" height={12} />
                </div>
              </div>
            </Card>
          ))}
        </div>
      </Card>
    </div>
  )
}
